import type { ResponseType } from '@/types/common';
import httpUtil from '@/utils/http';

const request = httpUtil.mfd.request;
const prefix = 'api/v1';

export interface QueryRecordType {
  versionId: number;
  testCount: number;
}

export interface EditTestPlanType {
  plan_id: number;
  name: string;
  description: string;
  status: string;
  Stage: string;
  plannedStartTime: number;
  plannedEndTime: number;
  actualStartTime: number;
  actualEndTime: number;
  creator: number;
  sysProjectID: number;
  testVersionID: number;
}

export interface pageType {
  size: number;
  page: number;
  project: string[];
  projectType: string;
  result: number;
  testType: string;
  testPhase: string;
}

export interface TestRecordType {
  projectId: number;
  projectName: string;
  versionId: number;
  newFeatureRemark: string;
  updateRemark: string;
  adviseRemark: string;
  testPackageUrl: string;
  softwareVersion?: string;
  hardwareVersion?: string;
  firmwareVersion?: string;
  testPurposeId?: number;
  testContentId?: number;
  projectPhaseId?: number;
  pluginVersion?: string;
  materielPhaseId?: number;
  structureVersion?: string;
}

export interface CreateSummaryType {
  versionId: number;
  recordId: number;
  bugData: string;
  summary: string;
}

export interface ReportEmailData {
  emailId: number;
  to: EmailType[];
  cc: EmailType[];
}

export interface EmailType {
  name: string;
  email: string;
}

const testManagementApi = {
  /**
   * @description 相关需求列表
   * @param versionId number
   */
  getSubReviewList(versionId: number): ResponseType {
    return request({
      url: prefix + `/demand/reviewList`,
      method: 'GET',
      params: {
        versionID: versionId,
      },
    });
  },

  /**
   * @description 获取测试计划列表
   */
  planList(params: any): ResponseType {
    return request({
      url: prefix + `/test/plans`,
      method: 'GET',
      params,
    });
  },

  /**
   * @description 根据项目id获取测试计划列表
   */
  planListBySysId(params: any): ResponseType {
    return request({
      url: prefix + `/test/plans`,
      method: 'GET',
      params,
    });
  },

  /**
   * @description 获取测试计划列表
   */
  planListByVersion(params: { sysProjectId: number; projectVersionId: number }): ResponseType {
    return request({
      url: prefix + `/test/plan/version/${params.sysProjectId}/${params.projectVersionId}`,
      method: 'GET',
    });
  },

  /**
   * @description 项目用例目录
   */
  ProjectNodeTree(project_id: any): ResponseType {
    return request({
      url: prefix + `/test/node/project/list/${project_id}`,
      method: 'GET',
    });
  },

  /**
   * @description 获取测试计划数据统计
   */
  GetPlanStatistics(plan_id: any): ResponseType {
    return request({
      url: prefix + `/test/plan/${plan_id}/statistics`,
      method: 'GET',
    });
  },

  /**
   * @description 获取项目用例目录
   */
  PlanNodeTree(plan_id: any): ResponseType {
    return request({
      url: prefix + `/test/node/plan/list/${plan_id}`,
      method: 'GET',
    });
  },

  /**
   * @description 获取测试计划报告信息
   */
  PlanReport(plan_id: any): ResponseType {
    return request({
      url: prefix + `/test/plan/${plan_id}/report`,
      method: 'GET',
    });
  },

  /**
   * @description 获取测试计划报告测试结果
   */
  ModuleResult(params: any): ResponseType {
    return request({
      url: prefix + `/test/plan/${params.plan_id}/report/result`,
      method: 'GET',
      params: params,
    });
  },

  /**
   * @description 获取测试计划报告失败用例
   */
  ModuleFailureCase(params: any): ResponseType {
    return request({
      url: prefix + `/test/plan/${params.plan_id}/report/failureCase`,
      method: 'GET',
      params: params,
    });
  },

  /**
   * @description 获取执行用例详情
   * @param params 包含 plan_id, case_id 以及其他可选查询参数如 priority, status, search, nodeIds
   */
  GetExecuteCaseInfo(params: any): ResponseType {
    const query: any = { ...params };
    // 「下一条/上一条」由后端按当前筛选计算邻居用例，需与列表一致带上执行人筛选。
    // 列表页 filter 里执行人字段名为 executorName，后端识别的查询参数为 executor。
    if (params.executorName !== undefined && params.executorName !== '') {
      query.executor = params.executorName;
    }
    return request({
      url: prefix + `/test/plan/${params.plan_id}/case/${params.case_id}`,
      method: 'GET',
      params: query,
    });
  },

  /**
   * @description 获取未关联测试用例
   */
  PlanUnrelatedCaseList(params: any): ResponseType {
    const newParams: any = {
      page: params.pageIndex,
      size: params.pageSize,
    };
    if (params.node && params.node.length) {
      newParams['node'] = params.node;
    }
    // 转换name为search
    if (params.name !== undefined && params.name !== '') {
      newParams.search = params.name;
    }
    ['priority', 'status', 'tag'].forEach((key) => {
      if (params[key] !== undefined && params[key] !== '') {
        newParams[key] = params[key];
      }
    });
    return request({
      url: prefix + `/test/plan/${params.plan_id}/unrelated/case`,
      method: 'GET',
      params: newParams,
    });
  },

  /**
   * @description 获取关联测试用例
   */
  GetRelatedCaseList(params: any): ResponseType {
    const newParams: any = {
      page: params.pageIndex,
      size: params.pageSize,
    };
    if (params.node && params.node.length) {
      newParams['node'] = params.node;
    }
    if (params.case_id !== undefined && params.case_id !== '') {
      newParams.caseId = params.case_id;
    }
    // 转换name为search
    if (params.name !== undefined && params.name !== '') {
      newParams.search = params.name;
    }
    if (params.executorName !== undefined && params.executorName !== '') {
      newParams.executor = params.executorName;
    }
    ['priority', 'status', 'tag'].forEach((key) => {
      if (params[key] !== undefined && params[key] !== '') {
        newParams[key] = params[key];
      }
    });
    if (params.updatedAt !== undefined && params.updatedAt !== '') {
      newParams['updatedAt'] = params.updatedAt;
    }
    // 🆕 支持排除指定计划已关联的用例
    if (params.exclude_plan_id !== undefined && params.exclude_plan_id !== '') {
      newParams['exclude_plan_id'] = params.exclude_plan_id;
    }
    return request({
      url: prefix + `/test/plan/${params.plan_id}/related/case`,
      method: 'GET',
      params: newParams,
    });
  },

  // 关联测试用例
  RelateCase(params: any): ResponseType {
    return request({
      url: prefix + `/test/plan/${params.plan_id}/case`,
      method: 'POST',
      data: params,
    });
  },

  addTestPlan(params: any): ResponseType {
    return request({
      url: prefix + `/test/plan`,
      method: 'POST',
      data: params,
    });
  },

  /**
   * @description 编辑单条测试计划
   * @param params
   */
  editTestPlan(params: EditTestPlanType): ResponseType {
    return request({
      url: prefix + `/test/plan/${params.plan_id}`,
      method: 'PUT',
      data: params,
    });
  },

  // 更新测试计划执行状态
  updateTestPlanStatus(params: EditTestPlanType): ResponseType {
    return request({
      url: prefix + `/test/plan/${params.plan_id}/status`,
      method: 'PUT',
      data: params,
    });
  },

  // 更新测试计划执行结果
  UpdatePlanCaseResult(params: any): ResponseType {
    return request({
      url: prefix + `/test/plan/${params.plan_id}/case/${params.case_id}`,
      method: 'PUT',
      data: params,
    });
  },

  // 更新可靠性测试计划执行结果
  UpdateReliabilityPlanCaseResult(params: any): ResponseType {
    return request({
      url: prefix + `/test/plan/${params.plan_id}/reliability/case/${params.case_id}`,
      method: 'PUT',
      data: params,
    });
  },

  // 更新测试计划绑定问题
  UpdatePlanCaseIssues(params: any): ResponseType {
    return request({
      url: prefix + `/test/plan/${params.plan_id}/case/${params.case_id}/issues`,
      method: 'PUT',
      data: params,
    });
  },

  // 解绑测试计划执行结果关联用例信息
  UnbindPlanCaseIssue(params: any): ResponseType {
    return request({
      url: prefix + `/test/plan/${params.plan_id}/case/${params.case_id}`,
      method: 'POST',
      data: params,
    });
  },

  /**
   * @description 复制单条测试计划
   * @param params
   */
  copyTestPlan(params: EditTestPlanType): ResponseType {
    return request({
      url: prefix + `/test/plan/${params.plan_id}/copy`,
      method: 'PUT',
      data: params,
    });
  },

  /**
   * @description 获取单条测试计划
   * @param params
   */
  getTestPlanById(plan_id: any): ResponseType {
    return request({
      url: prefix + `/test/plan/${plan_id}`,
      method: 'GET',
    });
  },

  /**
   * @description 删除单条测试计划
   * @param params
   */
  deleteTestPlan(id: number): ResponseType {
    return request({
      url: prefix + `/test/plan/${id}`,
      method: 'DELETE',
    });
  },

  BatchDeleteRelatedCase(params: any): ResponseType {
    return request({
      url: prefix + `/test/plan/${params.plan_id}/case/batch/delete`,
      method: 'POST',
      data: params,
    });
  },

  // 批量修改属性
  BatchEditPlanCase(params: any): ResponseType {
    return request({
      url: prefix + `/test/plan/${params.plan_id}/case/batch/edit`,
      method: 'POST',
      data: params,
    });
  },

  getReportList(params: [pageType]): ResponseType {
    return request({
      url: prefix + `/reports`,
      method: 'GET',
      params: params,
    });
  },

  /**
   * @description 根据项目提测id获取测试报告id
   * @param params
   */
  recordListDetailIdToReportId(params: { projectId: number; versionId: number }): ResponseType {
    return request({
      url: prefix + `/reportId`,
      method: 'GET',
      params: params,
    });
  },

  /**
   * @description 获取测试报告发布时需要邮件通知的成员
   * @param projectId 系统项目id
   */
  getReportEmail(projectId: number, testTypeId: number): ResponseType {
    return request({
      url: prefix + '/reportEmail',
      method: 'GET',
      params: { projectId: projectId, testTypeId: testTypeId },
    });
  },

  /**
   * @description 获取项目各角色人员
   * @param id
   */
  getProjectUser(id: number): ResponseType {
    return request({
      url: prefix + `/sysProject/${id}/users`,
      method: 'GET',
    });
  },

  /**
   * @description 获取项目下前15个版本
   * @param id 系统项目id
   */
  getJiraVersion(id: number): ResponseType {
    return request({
      url: prefix + `/project/latestVersions`,
      method: 'GET',
      params: {
        project: id,
      },
    });
  },

  /**
   * @description 获取项目下所有版本
   * @param sysProjectId 系统项目id
   */
  getAllJiraVersionBasedOnProject(sysProjectId: number): ResponseType {
    return request({
      url: prefix + `/project/versions`,
      method: 'GET',
      params: {
        project: sysProjectId,
      },
    });
  },
};

export { testManagementApi };
